/**
 * Hook para gerenciar a listagem de arquivos favoritos do usuário
 */

import { useState, useEffect, useCallback } from 'react';
import { Arquivo, FiltrosArquivo, Paginacao, RespostaApiArquivos, ErroApi } from '../types/gerArquivos';
import { GerArquivosService } from '../services/gerArquivosService';
import { sendToTerminal } from '../lib/terminalLogger';

interface UseFavoritosReturn {
  arquivos: Arquivo[];
  paginacao: Paginacao;
  filtros: FiltrosArquivo;
  carregando: boolean;
  erro: ErroApi | null;
  atualizarFiltros: (novosFiltros: Partial<FiltrosArquivo>) => void;
  alternarFavorito: (arquivoId: string) => Promise<void>;
  recarregarArquivos: () => Promise<void>;
  irParaPagina: (pagina: number) => void;
}

export function useFavoritos(): UseFavoritosReturn {
  const [arquivos, setArquivos] = useState<Arquivo[]>([]);
  const [paginacao, setPaginacao] = useState<Paginacao>({
    paginaAtual: 1,
    itensPorPagina: 10,
    totalItens: 0,
    totalPaginas: 0
  });

  const [filtros, setFiltros] = useState<FiltrosArquivo>({
    searchTerm: '',
    dateFrom: '', // Não usar filtro de data para favoritos
    dateTo: '',
    favorito: true, // Sempre true para favoritos
    sort_by: 'created_at',
    sort_dir: 'desc'
  });
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<ErroApi | null>(null);

  const buscarFavoritos = useCallback(async () => {
    setCarregando(true);
    setErro(null);

    try {
      sendToTerminal('FAVORITOS:LIST_REQUEST', { filtros, paginacao });
      
      const response = await GerArquivosService.buscarArquivos(filtros, {
        paginaAtual: paginacao.paginaAtual,
        itensPorPagina: paginacao.itensPorPagina
      });
      
      if (response.success) {
        setArquivos(response.data.arquivos);
        setPaginacao(response.data.paginacao);
        sendToTerminal('FAVORITOS:LIST_OK', { count: response.data.arquivos.length });
      } else {
        const msg = response.error || 'Erro ao buscar favoritos';
        setErro({ codigo: 'LIST_ERROR', mensagem: msg });
        sendToTerminal('FAVORITOS:LIST_ERROR', { code: 'LIST_ERROR', message: msg });
      }
    } catch (error) {
      setErro({ 
        codigo: 'API_UNAVAILABLE', 
        mensagem: 'Erro de conexão com o servidor', 
        detalhes: error instanceof Error ? error.message : 'Erro desconhecido' 
      });
      sendToTerminal('FAVORITOS:LIST_ERROR', { code: 'API_UNAVAILABLE' });
    } finally {
      setCarregando(false);
    }
  }, [filtros, paginacao.paginaAtual, paginacao.itensPorPagina]);

  const alternarFavorito = useCallback(async (arquivoId: string) => {
    try {
      const arquivo = arquivos.find(a => a.id === arquivoId);
      if (!arquivo) return;

      const response = await GerArquivosService.alternarFavorito(arquivoId, !arquivo.favorito);

      if (response.success) {
        // Se foi desfavoritado, remover da lista
        if (arquivo.favorito) {
          setArquivos(prev => prev.filter(a => a.id !== arquivoId));
          setPaginacao(prev => ({ ...prev, totalItens: Math.max(0, prev.totalItens - 1) }));
        } else {
          setArquivos(prev => prev.map(a => 
            a.id === arquivoId ? { ...a, favorito: !a.favorito } : a
          ));
        }
        sendToTerminal('FAVORITOS:FAVORITO_OK', { id: arquivoId });
      } else {
        setErro({ codigo: 'VALIDATION_ERROR', mensagem: 'Erro ao atualizar favorito' });
      }
    } catch {
      setErro({ codigo: 'API_UNAVAILABLE', mensagem: 'Erro ao atualizar favorito' });
    }
  }, [arquivos]);

  const atualizarFiltros = useCallback((novosFiltros: Partial<FiltrosArquivo>) => {
    setFiltros(prev => ({ ...prev, ...novosFiltros, favorito: true }));
    setPaginacao(prev => ({ ...prev, paginaAtual: 1 }));
  }, []);

  const recarregarArquivos = useCallback(async () => { 
    await buscarFavoritos(); 
  }, [buscarFavoritos]);

  const irParaPagina = useCallback((pagina: number) => { 
    setPaginacao(prev => ({ ...prev, paginaAtual: pagina })); 
  }, []);

  useEffect(() => { 
    buscarFavoritos(); 
  }, [buscarFavoritos]);

  return { 
    arquivos, 
    paginacao, 
    filtros, 
    carregando, 
    erro, 
    atualizarFiltros, 
    alternarFavorito, 
    recarregarArquivos, 
    irParaPagina 
  };
}

